import { useLocale } from "../../../../hooks/useLocale";
import { msg } from "../../../../locales/translate.ts";
import { CircleAlert, CircleCheck, RotateCw, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GD } from "src/GD";
import { S_STATEMENT_CHANGED, S_STATEMENT_POPUP } from "src/view/reports/StatementPopup";
import { formatProfitAmount } from "src/view/statView/accountantData";
import { AccountingCard as Card, ReserveRefreshButton } from "src/view/statView/styled";
import styled from "styled-components";
import { formatStatementHealthTime, getStatementHealthRequest, parseClientStatementHealth, type IClientStatementHealth } from "./clientStatementHealthData";

const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 12px;
	.heading { display: flex; align-items: center; gap: 12px; min-width: 0; }
	.icon { display: flex; flex-shrink: 0; padding: 10px; border-radius: 12px; color: #6d5ce8; background: #f1effd; }
	h3 { margin: 0; font-size: 14px; font-weight: 600; color: #1a1d26; }
	.scope { margin-top: 4px; color: #848799; font-size: 13px; }
`;

const Summary = styled.div`
	display: flex;
	align-items: center;
	margin-top: 16px;
	.status { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 500; line-height: 1.5; }
	svg { flex-shrink: 0; }
`;

const Counts = styled.dl`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 12px;
	margin: 12px 0 0;
	> div { min-width: 0; padding: 12px 14px; border-radius: 10px; background: #f5f3ff; }
	> div.warning { background: #fff7e8; }
	> div.danger { background: #fff1f3; }
	dt { color: #667085; font-size: 12px; line-height: 18px; }
	dd { margin: 6px 0 0; font-size: 26px; font-weight: 600; color: #1a1d26; font-variant-numeric: tabular-nums; white-space: nowrap; }
	.warning dd { color: #a15c00; }
	.danger dd { color: #b42332; }
	@media (max-width: 680px) { grid-template-columns: minmax(0, 1fr); }
`;

const Meta = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 4px 20px;
	margin-top: 12px;
	color: #667085;
	font-size: 12px;
	line-height: 1.5;
	time { color: #344054; font-weight: 500; white-space: nowrap; }
`;

const Findings = styled.details`
	margin-top: 14px;
	min-width: 0;
	summary { width: fit-content; max-width: 100%; cursor: pointer; font-size: 13px; font-weight: 600; color: #344054; line-height: 1.5; }
	summary:focus-visible { outline: 2px solid #6374d9; outline-offset: 3px; }
`;

const TableScroll = styled.div`
	max-width: 100%;
	overflow-x: auto;
	margin-top: 10px;
	border: 1px solid #e7ebf1;
	border-radius: 8px;
	table { width: 100%; min-width: 720px; border-collapse: collapse; font-size: 12px; }
	th, td { padding: 10px 12px; border-bottom: 1px solid #edf0f4; text-align: left; white-space: nowrap; }
	th { background: #fafbfc; color: #667085; font-weight: 500; }
	td { color: #344054; font-variant-numeric: tabular-nums; }
	th:last-child, td:last-child { text-align: right; }
	tbody:last-child tr:last-child td { border-bottom: 0; }
	tbody.pair + tbody.pair { border-top: 2px solid #e7ebf1; }
	.range { display: flex; flex-direction: column; gap: 3px; }
	button { border: 0; padding: 0; background: transparent; color: #25638e; font: inherit; font-weight: 500; cursor: pointer; text-underline-offset: 3px; }
	button:hover { text-decoration: underline; }
	button:focus-visible { outline: 2px solid #6374d9; outline-offset: 4px; border-radius: 2px; }
`;

interface IClientStatementHealthCardProps {
	clientId: number;
	projectId: number;
	userId: number;
}

export const ClientStatementHealthCard = ({ clientId, projectId, userId }: IClientStatementHealthCardProps) => {
	useLocale();
	const { t } = useTranslation();
	const [attempt, setAttempt] = useState(0);
	const [state, setState] = useState<{ busy: boolean; data?: IClientStatementHealth; error?: boolean }>({ busy: true });

	useEffect(() => {
		const controller = new AbortController();
		const logout = GD.S_AUTH_LOG_OUT.subscribe(() => { controller.abort(); setState({ busy: false }); });
		const changed = S_STATEMENT_CHANGED.subscribe(statement => {
			if (statement.client_id === clientId && !controller.signal.aborted) setAttempt(value => value + 1);
		});
		setState({ busy: true });
		const load = async () => {
			const checkContext = async () => {
				const [profile, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
				if (controller.signal.aborted || profile?.id !== userId || project?.id !== projectId) throw new Error("Context changed");
			};
			try {
				await checkContext();
				const response = await GD.REQ_API.request({ ...getStatementHealthRequest(projectId), signal: controller.signal });
				await checkContext();
				if (response?.error) throw new Error(msg("statementHealthRequestFailed"));
				const data = parseClientStatementHealth(response?.data, clientId);
				if (!data) throw new Error(msg("unexpectedStatementHealthResponse"));
				if (!controller.signal.aborted) setState({ busy: false, data });
			} catch {
				if (!controller.signal.aborted) setState({ busy: false, error: true });
			}
		};
		void load();
		return () => {
			controller.abort();
			GD.S_AUTH_LOG_OUT.unsubscribe(logout);
			S_STATEMENT_CHANGED.unsubscribe(changed);
		};
	}, [clientId, projectId, userId, attempt]);

	const data = state.data;
	const unbilledDays = data?.unbilled.reduce((total, item) => total + item.days, 0) ?? 0;
	const hasIssues = !!data && (data.stuck.length > 0 || data.overlapping.length > 0 || unbilledDays > 0);
	const money = (value: number) => formatProfitAmount(value, "EUR", t("accountant.locale"));
	const range = (from: string, to: string) => <div className="range"><time dateTime={from}>{formatStatementHealthTime(from, t("accountant.locale"))}</time><time dateTime={to}>{formatStatementHealthTime(to, t("accountant.locale"))}</time></div>;
	const statementButton = (id: number) => <button type="button" title={t("accountant.openStatement", { id })} aria-label={t("accountant.openStatement", { id })} onClick={() => S_STATEMENT_POPUP.invoke({ id })}>{id}</button>;

	return <Card role="region" aria-label={t("accountant.healthTitle")} aria-busy={state.busy} style={{ height: "auto", minWidth: 0 }}>
		<Header>
			<div className="heading"><div className="icon"><ShieldCheck size={20} aria-hidden="true" /></div><div><h3>{t("accountant.healthTitle")}</h3><div className="scope">{t("accountant.healthScope")}</div></div></div>
			<ReserveRefreshButton $busy={state.busy} disabled={state.busy} title={t(state.error ? "accountant.retry" : "accountant.refresh")} aria-label={t(state.error ? "accountant.retry" : "accountant.refresh")} onClick={() => setAttempt(value => value + 1)}><RotateCw size={16} aria-hidden="true" /></ReserveRefreshButton>
		</Header>
		<div role="status" aria-live="polite">
			{state.busy && <div style={{ marginTop: 14, color: "#667085", fontSize: 13 }}>{t("accountant.healthLoading")}</div>}
			{state.error && <div style={{ marginTop: 14, color: "#b42332", fontSize: 13 }}>{t("accountant.healthError")}</div>}
			{data && <Summary>
				<div className="status" style={{ color: hasIssues ? "#a15c00" : "#157347" }}>{hasIssues ? <CircleAlert size={17} aria-hidden="true" /> : <CircleCheck size={17} aria-hidden="true" />}{t(hasIssues ? "accountant.healthIssues" : "accountant.healthClean")}</div>
			</Summary>}
		</div>
		{data && <>
			<Counts>
				<div className={data.stuck.length ? "warning" : undefined}><dt>{t("accountant.healthStuck")}</dt><dd>{data.stuck.length}</dd></div>
				<div className={data.overlapping.length ? "danger" : undefined}><dt>{t("accountant.healthOverlapping")}</dt><dd>{data.overlapping.length}</dd></div>
				<div className={unbilledDays ? "warning" : undefined}><dt>{t("accountant.healthUnbilled")}</dt><dd>{unbilledDays}</dd></div>
			</Counts>
			<Meta><span>{t("accountant.healthThreshold", { count: data.stuckDays })}</span><span>{t("accountant.healthAsOf")} <time dateTime={data.asOf.replace(" ", "T")}>{data.asOf}</time></span></Meta>
			{!!data.stuck.length && <Findings>
				<summary>{t("accountant.healthStuck")} ({data.stuck.length})</summary>
				<TableScroll><table aria-label={t("accountant.healthStuck")}>
					<thead><tr><th>{t("accountant.statement")}</th><th>{t("accountant.type")}</th><th>{t("accountant.status")}</th><th>{t("accountant.healthAge")}</th><th>{t("accountant.healthPeriod")}</th><th>{t("accountant.net")}</th></tr></thead>
					<tbody>{data.stuck.map(statement => <tr key={statement.id}><td>{statementButton(statement.id)}</td><td>{statement.type || "—"}</td><td>{statement.status}</td><td>{statement.ageDays}</td><td>{range(statement.from, statement.to)}</td><td>{money(statement.net)}</td></tr>)}</tbody>
				</table></TableScroll>
			</Findings>}
			{!!data.overlapping.length && <Findings>
				<summary>{t("accountant.healthOverlapping")} ({data.overlapping.length})</summary>
				<TableScroll><table aria-label={t("accountant.healthOverlapping")}>
					<thead><tr><th>{t("accountant.healthPair")}</th><th>{t("accountant.statement")}</th><th>{t("accountant.status")}</th><th>{t("accountant.healthPeriod")}</th><th>{t("accountant.net")}</th></tr></thead>
					{data.overlapping.map((pair, index) => <tbody className="pair" key={`${pair.first.id}:${pair.second.id}`}>
						<tr><td rowSpan={2}>{index + 1}</td><td>{statementButton(pair.first.id)}</td><td>{pair.first.status}</td><td>{range(pair.first.from, pair.first.to)}</td><td>{money(pair.first.net)}</td></tr>
						<tr><td>{statementButton(pair.second.id)}</td><td>{pair.second.status}</td><td>{range(pair.second.from, pair.second.to)}</td><td>{money(pair.second.net)}</td></tr>
					</tbody>)}
				</table></TableScroll>
			</Findings>}
			{!!data.unbilled.length && <Findings>
				<summary>{t("accountant.healthUnbilled")} ({unbilledDays})</summary>
				<TableScroll><table aria-label={t("accountant.healthUnbilled")}>
					<thead><tr><th>{t("accountant.healthDays")}</th><th>{t("accountant.healthPeriod")}</th><th>{t("accountant.net")}</th></tr></thead>
					<tbody>{data.unbilled.map((item, index) => <tr key={index}><td>{item.days}</td><td>{range(item.from, item.to)}</td><td>{money(item.net)}</td></tr>)}</tbody>
				</table></TableScroll>
			</Findings>}
		</>}
	</Card>;
};